/* ============================================================================
 * cart-aggregation.js — v5, 23 Jul 2026 (Purezen)
 * ----------------------------------------------------------------------------
 * Companion to the merged-row cart display. Only rows representing 2+ real
 * cart lines carry data-agg-* attributes; every other row, and every other
 * page, is ignored completely.
 *
 * v4 -> v5: two robustness fixes found in review.
 *   1. The debounce timer was global, so changing product A and then product
 *      B inside 250ms silently dropped A's change. Timers are now per product.
 *   2. Two cart writes could overlap and race. Writes are now queued so they
 *      run strictly one after another.
 * ========================================================================== */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var timers = {};
  var chain = Promise.resolve();

  function getCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function post(path, payload) {
    return fetch(root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) { return r.json(); });
  }

  /* The theme's own in-place redraw. Both the cart page and the drawer listen
   * for this, so nothing reloads and the drawer stays open. */
  function redraw() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function fail(err) {
    console.error('[cart-aggregation]', err);
    redraw();
  }

  /* Serialise every cart write so two clicks can never interleave. */
  function enqueue(task) {
    chain = chain.then(task).catch(fail);
    return chain;
  }

  function markBusy(el) {
    var row = el.closest ? el.closest('.product-cart-item, tr') : null;
    if (row) { row.classList.add('thb-loading'); row.classList.add('loading'); }
  }

  function linesFor(cart, variantId) {
    var out = [];
    cart.items.forEach(function (it) {
      if (String(it.variant_id) === String(variantId)) {
        out.push({ key: it.key, qty: it.quantity });
      }
    });
    return out;
  }

  /* Move the whole group to `target` units, reading live quantities from the
   * cart rather than trusting whatever is currently rendered. */
  function setTotal(variantId, target) {
    return getCart().then(function (cart) {
      var lines = linesFor(cart, variantId);
      if (!lines.length) { redraw(); return; }

      var current = 0;
      lines.forEach(function (l) { current += l.qty; });
      if (target === current) { redraw(); return; }

      var updates = {};

      if (target > current) {
        var last = lines[lines.length - 1];
        updates[last.key] = last.qty + (target - current);
      } else {
        var remaining = current - target;
        for (var i = lines.length - 1; i >= 0 && remaining > 0; i--) {
          var take = Math.min(remaining, lines[i].qty);
          updates[lines[i].key] = lines[i].qty - take;
          remaining -= take;
        }
      }

      return post('cart/update.js', { updates: updates }).then(redraw);
    }).catch(fail);
  }

  /* Quantity change on a MERGED row only. Capture phase runs before the
   * theme's own listener, so stopPropagation prevents double handling. */
  document.addEventListener('change', function (event) {
    var input = event.target.closest && event.target.closest('input[data-agg-keys]');
    if (!input) return;

    event.stopPropagation();

    var variantId = input.getAttribute('data-agg-variant');
    var target = parseInt(input.value, 10);
    if (!variantId || isNaN(target) || target < 0) return;

    markBusy(input);

    clearTimeout(timers[variantId]);
    timers[variantId] = setTimeout(function () {
      enqueue(function () { return setTotal(variantId, target); });
    }, 250);
  }, true);

  /* Trash on a MERGED row: clear every underlying line together. */
  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[data-agg-keys]');
    if (!link) return;

    event.preventDefault();
    event.stopPropagation();

    var updates = {};
    (link.getAttribute('data-agg-keys') || '').split(',').forEach(function (k) {
      if (k) { updates[k] = 0; }
    });
    if (!Object.keys(updates).length) { redraw(); return; }

    markBusy(link);
    enqueue(function () {
      return post('cart/update.js', { updates: updates }).then(redraw);
    });
  }, true);
})();
